// client/src/pages/CreateQRCode.js

import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode.react';
import { useUser } from '@clerk/clerk-react';
import qrcode from 'qrcode';
import { Link } from 'react-router-dom';

function CreateQRCode() {
  // Move useUser() hook to the top level of the component
  const { user } = useUser();
  
  const [name, setName] = useState('');
  const [type, setType] = useState('url');
  const [content, setContent] = useState('');
  const [shape, setShape] = useState('square');
  const [showName, setShowName] = useState(true);
  const [showURL, setShowURL] = useState(false);
  const [foregroundColor, setForegroundColor] = useState('#000000');
  const [backgroundColor, setBackgroundColor] = useState('#FFFFFF');
  const [textColor, setTextColor] = useState('#000000');
  const [logoFile, setLogoFile] = useState(null);
  const [logo, setLogo] = useState(null);
  const [qrCodesCreated, setQrCodesCreated] = useState(0);
  const [qrCodeLimit, setQrCodeLimit] = useState(3); // Default free plan limit
  const [account, setAccount] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(14);
  const [loading, setLoading] = useState(false);
  const [upgadeNeeded, setUpgradeNeeded] = useState(false);
  // Add error states
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [dragActive, setDragActive] = useState(false);

const handleDrag = (e) => {
  e.preventDefault();
  e.stopPropagation();
  if (e.type === "dragenter" || e.type === "dragover") {
    setDragActive(true);
  } else if (e.type === "dragleave") {
    setDragActive(false);
  }
};

const handleDrop = (e) => {
  e.preventDefault();
  e.stopPropagation();
  setDragActive(false);
  
  if (e.dataTransfer.files && e.dataTransfer.files[0]) {
    // Handle the dropped file(s)
    const file = e.dataTransfer.files[0];
    setLogoFile(file);
    setLogo(URL.createObjectURL(file));
  }
};

  const [vCardData, setVCardData] = useState({
    name: '',
    phone: '',
    email: '',
    company: ''
  });
  
  // Handler for vCard field changes
  const handleVCardChange = (field, value) => {
    setVCardData({
      ...vCardData,
      [field]: value
    });
  };
  
  // Function to format vCard data
  const formatVCardData = () => {
    if (type !== 'vcard') return '';
    
    let vcard = 'BEGIN:VCARD\nVERSION:3.0\n';
    
    if (vCardData.name) vcard += `FN:${vCardData.name}\n`;
    if (vCardData.phone) vcard += `TEL:${vCardData.phone}\n`;
    if (vCardData.email) vcard += `EMAIL:${vCardData.email}\n`;
    if (vCardData.company) vcard += `ORG:${vCardData.company}\n`;
    
    vcard += 'END:VCARD';
    
    return vcard;
  };

  // Add useEffect to fetch account and QR code count
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!user) return;
        
        console.log("Fetching data for user:", user.id);
        
        // First, get the user document to find the account_id
        const userResponse = await fetch(`/api/users/by-clerk/${user.id}`);
        
        if (userResponse.ok) {
          const userData = await userResponse.json();
          console.log('User data:', userData);
          
          if (userData && userData.account_id) {
            // Now fetch the account using the account_id
            const accountResponse = await fetch(`/api/account/${userData.account_id}`);
            
            if (accountResponse.ok) {
              const accountData = await accountResponse.json();
              console.log('Account data:', accountData);
              setAccount(accountData);
              
              // Set QR code limit based on plan
              if (accountData.plan === 'free_basic' || accountData.plan === 'basic_plus') {
                setQrCodeLimit(3);
              } else if (accountData.plan && accountData.plan.startsWith('basic_')) {
                setQrCodeLimit(3);
              } else if (accountData.plan && accountData.plan.startsWith('plus_')) {
                setQrCodeLimit(100);
              } else {
                setQrCodeLimit(500); // Unlimited for business plans
              }
            }
          }
        }
        
        // Fetch QR codes to count them
        const qrResponse = await fetch(`/api/qrcodes?user_id=${user.id}`);
        if (qrResponse.ok) {
          const qrData = await qrResponse.json();
          console.log(`Found ${qrData.length} QR codes for user ${user.id}`);
          setQrCodesCreated(qrData.length);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();
  }, [user]);

  useEffect(() => {
    // Check for pending QR data from the landing page
    const pendingQRData = localStorage.getItem('pendingQR');
    
    if (pendingQRData) {
      try {
        const qrData = JSON.parse(pendingQRData);
        
        // Populate form with saved data
        setName(qrData.name || '');
        setType(qrData.type || 'url');
        setContent(qrData.content || '');
        setShape(qrData.shape || 'square');
        setShowName(qrData.showName !== undefined ? qrData.showName : true);
        setShowURL(qrData.showURL !== undefined ? qrData.showURL : true);
        setForegroundColor(qrData.foregroundColor || '#000000');
        setBackgroundColor(qrData.backgroundColor || '#FFFFFF');
        setTextColor(qrData.textColor || '#000000');
        
        // Handle vCard data if present
        if (qrData.vCardData && qrData.type === 'vcard') {
          setVCardData(qrData.vCardData);
        }
        
        // Clear storage after using it
        localStorage.removeItem('pendingQR');
      } catch (error) {
        console.error('Error loading pending QR data:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (type === 'vcard') {
      // Automatically disable showURL for contact cards
      setShowURL(false);
    }
  }, [type]);
  
  const getShowContentLabel = () => {
    switch(type) {
      case 'phone':
        return 'Show Phone Number';
      case 'email':
        return 'Show Email Address';
      case 'text':
        return 'Show Text';
      case 'sms':
        return 'Show SMS Number';
      default:
        return 'Show URL';
    }
  };
  
  // Validate form fields and set error messages
  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (type === 'vcard') {
      // Validate vCard fields
      if (!vCardData.name.trim()) {
        newErrors.vCardName = 'Contact name is required';
      }
      // Add other validations as needed
    } else {
      // Only validate content for non-vcard types
      if (!content.trim()) {
        newErrors.content = 'Content is required';
      } else if (type === 'url' && !content.includes('.')) {
        newErrors.content = 'Please enter a valid URL';
      } else if (type === 'phone' && !/^[+]?[\d\s()-]{7,}$/.test(content)) {
        newErrors.content = 'Please enter a valid phone number';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const isValid = () => {
    return name.trim() !== '' && content.trim() !== '';
  };
  
  // Function to format content based on type
  const getFormattedContent = () => {
    if (type === 'vcard') {
      return formatVCardData();
    }
    
    if (!content) return 'https://example.com';
    
    if (type === 'phone') {
      // Format phone with tel: protocol
      const cleanedPhone = content.replace(/(?!^\+)[^\d]/g, '');
      return `tel:${cleanedPhone}`;
    } else if (type === 'url' && !content.startsWith('http://') && !content.startsWith('https://')) {
      return `${content}`;  // This is correct
    }
    
    return content;
  };

  // Scroll to field with error
  const scrollToField = (fieldId) => {
    const element = document.getElementById(fieldId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus();
    }
  };

  const downloadQRCodeDirect = (qrObject) => {
    // Debug log
    console.log("QR object for direct download:", qrObject);
    
    const shape = qrObject.shape || 'square';
    const bgColor = qrObject.backgroundColor || '#f1f1f4';
    const fgColor = qrObject.foregroundColor || '#000000';
    const textColor = qrObject.textColor || '#000000'; // Define textColor from qrObject
    
    try {
      // Create a canvas
      const canvas = document.createElement('canvas');
      const size = 1024; // High quality
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      
      // Draw background shape
      ctx.fillStyle = bgColor;
      if (shape === 'circle') {
        ctx.beginPath();
        ctx.arc(size/2, size/2, size/2, 0, Math.PI * 2);
        ctx.fill();
      } else if (shape === 'rounded') {
        const radius = size / 10;
        ctx.beginPath();
        ctx.moveTo(radius, 0);
        ctx.lineTo(size - radius, 0);
        ctx.quadraticCurveTo(size, 0, size, radius);
        ctx.lineTo(size, size - radius);
        ctx.quadraticCurveTo(size, size, size - radius, size);
        ctx.lineTo(radius, size);
        ctx.quadraticCurveTo(0, size, 0, size - radius);
        ctx.lineTo(0, radius);
        ctx.quadraticCurveTo(0, 0, radius, 0);
        ctx.fill();
      } else {
        ctx.fillRect(0, 0, size, size);
      }
      
      // Use qrcode library to get QR code data
      qrcode.toCanvas(canvas, qrObject.targetUrl, {
        width: size * 0.7,
        margin: 0,
        color: {
          dark: fgColor,
          light: 'transparent'
        },
        errorCorrectionLevel: 'H'
      }, function(error) {
        if (error) {
          console.error("Error generating QR code:", error);
          return;
        }

        // FIXED: Use showURL instead of setShowURL
        if (qrObject.targetUrl && qrObject.showURL) {
          ctx.fillStyle = textColor;
          ctx.font = 'bold 75px';
          ctx.textAlign = 'center';
          ctx.fillText(qrObject.targetUrl, size/2, size - 50);
        }
        
        // Add name if provided
        if (qrObject.qr_name && qrObject.showName) {
          ctx.fillStyle = textColor;
          ctx.font = 'bold 75px';
          ctx.textAlign = 'center';
          ctx.fillText(qrObject.qr_name, size/2, size - 100);
        }
        
        // Download the image
        const link = document.createElement('a');
        link.download = `${qrObject.qr_name || 'qrcode'}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    } catch (error) {
      console.error("Error creating QR code:", error);
      alert("There was an error creating your QR code. Please try again.");
    }
  };

  // In your Create QR Code component
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("Form submission started");
    
    // First validate the form
    const isFormValid = validateForm();
    
    if (!isFormValid) {
      setShowErrors(true);
      
      // Scroll to the first field with an error
      if (errors.name) {
        scrollToField('qr-name-field');
      } else if (errors.content) {
        scrollToField('qr-content-field');
      } else if (errors.vCardName) {
        // Add handling for vCard field errors
        const vCardNameField = document.querySelector('input[placeholder="John Doe"]');
        if (vCardNameField) {
          vCardNameField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          vCardNameField.focus();
        }
      }
      
      return;
    }
    
    // Check if user has reached their QR code limit
    if (qrCodesCreated >= qrCodeLimit) {
      alert('You have reached your QR code limit. Please upgrade your plan to create more QR codes.');
      setUpgradeNeeded(true);
      return;
    }
    
    setLoading(true); // Set loading state to true
      
    try {
      // Get formatted content
      const formattedContent = getFormattedContent();
      console.log("Formatted content:", formattedContent);
      
      // Create FormData object
      const formData = new FormData();
      formData.append('qr_name', name);
      formData.append('targetUrl', formattedContent); 
      formData.append('qrType', type);
      formData.append('user_id', user ? user.id : "fixed_user_123");
      formData.append('email_address', user ? user.primaryEmailAddress.emailAddress : "user@example.com");
      formData.append('shape', shape);
      formData.append('dotStyle', shape);
      formData.append('foregroundColor', foregroundColor);
      formData.append('backgroundColor', backgroundColor);
      formData.append('textColor', textColor);
      formData.append('showName', showName.toString());
      formData.append('showURL', showURL.toString());

      // If it's a vCard, append the vCard data
      if (type === 'vcard') {
        formData.append('vCardName', vCardData.name);
        formData.append('vCardPhone', vCardData.phone || '');
        formData.append('vCardEmail', vCardData.email || '');
        formData.append('vCardCompany', vCardData.company || '');
      }

      // Debug data
      console.log("User ID:", user ? user.id : "fixed_user_123");
      console.log("Email:", user ? user.primaryEmailAddress.emailAddress : "user@example.com");
      
      // Add logo file if present
      if (logoFile) {
        formData.append('logo', logoFile);
        console.log('Added logo file to form data:', logoFile.name);
      }

      // Debug FormData
      console.log("Form data entries:");
      for (let pair of formData.entries()) {
        console.log(`${pair[0]}: ${pair[1]}`);
      }

      console.log("Sending POST request to /api/qrcodes");
      
      const response = await fetch('/api/qrcodes', {
        method: 'POST',
        body: formData,
      });
      
      console.log("Response status:", response.status);
      
      if (response.ok) {
        try {
          const responseData = await response.json();
          console.log("Success response:", responseData);
          alert('QR code created successfully!');
          window.location.href = '/dashboard';
        } catch (jsonError) {
          console.error("Error parsing success response:", jsonError);
          alert('QR code created, but there was an issue with the server response.');
          window.location.href = '/dashboard';
        }
      } else {
        try {
          const errorData = await response.json();
          console.error("Error response:", errorData);
          alert(`Error: ${errorData.error || 'Unknown error'}`);
        } catch (jsonError) {
          console.error("Error parsing error response:", jsonError);
          const errorText = await response.text();
          console.error("Raw error response:", errorText);
          alert('Failed to create QR code. Server returned an invalid response.');
        }
      }
    } catch (error) {
      console.error('Error creating QR code:', error);
      alert('Failed to create QR code. Please try again.');
    }
    finally {
      setLoading(false); // Reset loading state
    }
  };
  
  // Handle logo file selection
  const handleLogoChange = (e) => {
    if (e.target.files[0]) {
      setLogoFile(e.target.files[0]);
      setLogo(URL.createObjectURL(e.target.files[0]));
    }
  };

  // Configure imageSettings for QRCode if logo is set
  const getQRCodeImageSettings = () => {
    if (logo) {
      return {
        src: logo,
        width: 40,
        height: 40,
        excavate: true,
        margin: 30
      };
    }
    return undefined;
  };

  return (
    <div className="mt-6 max-w-5xl mx-auto px-3 sm:px-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-0">Create a QR Code</h1>
        </div>

      <div className="flex justify-between items-center mb-6">
        <div className="mr-4">
          <p className="text-sm font-medium text-gray-700">
            QR Codes Created: <span className="font-bold">{qrCodesCreated} of {qrCodeLimit}</span>
          </p>
        </div>
        
        <div className="flex-grow">
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-blue-600 h-2.5 rounded-full" 
              style={{ width: `${Math.min(qrCodesCreated / qrCodeLimit * 100, 100)}%` }}
            ></div>
          </div>
        </div>
        
        {account && qrCodesCreated >= qrCodeLimit && (
          <div className="ml-4">
            <Link 
              to="/upgrade" 
              className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
            >
              Max Reached. Upgrade
            </Link>
          </div>
        )}
      </div>

      {/* Main content area - MADE RESPONSIVE HERE */}
      <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
        {/* Form section - MADE RESPONSIVE HERE */}
        <div className="md:col-span-5 bg-white rounded-lg shadow-sm p-3 md:p-5 mb-6">
          <div>
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              {/* QR Code Usage Indicator */}

              <div className="mb-1">
                <label className="block mt-1 text-m text-gray-700 font-semibold mb-1">Name:</label>
                <input
                  id="qr-name-field"
                  type="text"
                  className={`w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    showErrors && errors.name ? 'border-red-500' : ''
                  }`}
                  placeholder="My QR Code"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) {
                      const newErrors = {...errors};
                      delete newErrors.name;
                      setErrors(newErrors);
                    }
                  }}
                />
                {showErrors && errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>
              
              {/* Desktop-only display options (hidden on mobile) */}
              <div className="hidden md:block">
                <div className="flex items-center block text-sm text-gray-700 font-medium mb-8">
                  <input
                    type="checkbox"
                    id="showName"
                    className="mr-2"
                    checked={showName}
                    onChange={(e) => setShowName(e.target.checked)}
                  />
                  <label htmlFor="showName">Show Name</label>
                </div>
              </div>

              {/* QR Code Type Selector Buttons */}
              <div className="mb-4">
                <label className="block mt-5 text-m text-gray-700 font-semibold mb-3">Select what your QR code will do:</label>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <button
                    type="button"
                    className={`px-6 py-4 border-2 rounded-lg font-medium transition duration-200 flex flex-col items-center justify-center ${
                      type === 'url' 
                        ? 'bg-blue-50 border-blue-500 text-blue-600 font-semibold' 
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => setType('url')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 015.656 0l4 4a4 4 0 01-5.656 5.656l-1.102-1.101" />
                    </svg>
                    Link to a Website
                  </button>
                  
                  <button
                    type="button"
                    className={`px-6 py-4 border-2 rounded-lg font-medium transition duration-200 flex flex-col items-center justify-center ${
                      type === 'phone' 
                        ? 'bg-blue-50 border-blue-500 text-blue-600 font-semibold' 
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => setType('phone')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Call a Phone Number
                  </button>
                  
                  <button
                    type="button"
                    className={`px-6 py-4 border-2 rounded-lg font-medium transition duration-200 flex flex-col items-center justify-center ${
                      type === 'text' 
                        ? 'bg-blue-50 border-blue-500 text-blue-600 font-semibold' 
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => setType('text')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                    </svg>
                    Display Text
                  </button>
                  
                  <button
                    type="button"
                    className={`px-6 py-4 border-2 rounded-lg font-medium transition duration-200 flex flex-col items-center justify-center ${
                      type === 'vcard' 
                        ? 'bg-blue-50 border-blue-500 text-blue-600 font-semibold' 
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => setType('vcard')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                    </svg>
                    Create a Contact Card
                  </button>
                </div>
                
                {/* Dynamic input fields based on selected type */}
                {type === 'vcard' ? (
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <h3 className="font-semibold text-gray-700">Contact Information</h3>
                    
                    <div>
                      <label className="block text-sm text-gray-700 font-medium mb-1">Full Name:</label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="John Doe"
                        value={vCardData?.name || ''}
                        onChange={(e) => handleVCardChange('name', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-700 font-medium mb-1">Phone:</label>
                      <input
                        type="tel"
                        className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+1 (123) 456-7890"
                        value={vCardData?.phone || ''}
                        onChange={(e) => handleVCardChange('phone', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-700 font-medium mb-1">Email:</label>
                      <input
                        type="email"
                        className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="john.doe@example.com"
                        value={vCardData?.email || ''}
                        onChange={(e) => handleVCardChange('email', e.target.value)}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-700 font-medium mb-1">Company (optional):</label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Company Name"
                        value={vCardData?.company || ''}
                        onChange={(e) => handleVCardChange('company', e.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-m text-gray-700 font-semibold mb-1">
                      {type === 'url' ? 'URL' : type === 'phone' ? 'Phone Number' : 'Text'}:
                    </label>
                    <input
                      id="qr-content-field"
                      type={type === 'phone' ? 'tel' : 'text'}
                      className={`w-full px-4 py-2 font-medium border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        showErrors && errors.content ? 'border-red-500' : ''
                      }`}
                      placeholder={type === 'url' ? 'www.mywebsite.com' : type === 'phone' ? '+1234567890' : 'Your text here'}
                      value={content}
                      onChange={(e) => {
                        setContent(e.target.value);
                        if (errors.content) {
                          const newErrors = {...errors};
                          delete newErrors.content;
                          setErrors(newErrors);
                        }
                      }}
                    />
                    {showErrors && errors.content && (
                      <p className="text-red-500 text-sm mt-1">{errors.content}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop-only showURL control (hidden on mobile) */}
              {type !== 'vcard' && (
                <div className="hidden md:block">
                  <div className="flex items-center block text-sm text-gray-700 font-medium mb-8">
                    <input
                      type="checkbox"
                      id="showURL"
                      className="mr-2"
                      checked={showURL}
                      onChange={(e) => setShowURL(e.target.checked)}
                    />
                    <label htmlFor="showURL">{getShowContentLabel()}</label>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="pt-3 block mt-5 text-m text-gray-700 font-semibold mb-2">Add Logo or Image:</label>
                <div 
                  className="flex items-center justify-center w-full"
                  onDragEnter={handleDrag}
                >
                  <label 
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
                      dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className={`w-8 h-8 mb-3 ${dragActive ? "text-blue-500" : "text-gray-500"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                      </svg>
                      <p className="mb-1 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">SVG, PNG, JPG or GIF</p>
                    </div>
                    <input 
                      type="file" 
                      className="hidden"
                      accept="image/*"
                      onChange={handleLogoChange}
                      name="logo"
                    />
                  </label>
                </div>
                {logo && (
                  <div className="mt-3 flex items-center">
                    <img src={logo} alt="Logo preview" className="h-8 w-8 object-cover mr-2 rounded" />
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-600 font-medium">Logo added successfully!</span>
                      <button 
                        onClick={() => {
                          setLogo(null);
                          setLogoFile(null);
                        }}
                        className="text-xs text-red-500 hover:text-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="p-3"></div>
              
              {qrCodesCreated >= qrCodeLimit && account?.plan === 'free_trial' && (
                <Link
                  to="/upgrade"
                  className="block w-full text-center mt-3 text-blue-600 hover:text-blue-800 font-medium"
                >
                  View Upgrade Options
                </Link>
              )}
            </form>
          </div>
        </div>

        {/* Preview section - MADE RESPONSIVE HERE */}
        <div className="md:col-span-3 flex flex-col items-center">
          {/* QR Code Preview with styling */}
          <div 
            className={`qr-container p-10 ${shape === 'circle' ? 'rounded-full' : shape === 'rounded' ? 'rounded-2xl' : ''}`}
            style={{ 
              backgroundColor: backgroundColor || '#f1f1f4',
              ...(shape === 'circle' ? {
                width: '310px',  
                height: '310px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              } : {})
            }}
          >
            <div className="relative">
              <QRCode 
                value={getFormattedContent()} 
                size={150}
                fgColor={foregroundColor}
                bgColor="transparent"
                level="H"
                renderAs="svg"
              />
              
              {/* Logo overlay */}
              {logo && (
                <div className="absolute inset-0 flex items-center justify-center">
                  {/* Logo background */}
                  <div 
                    className={`${
                      shape === 'circle' ? 'rounded-full' : 
                      shape === 'rounded' ? 'rounded-lg' : 
                      'rounded-none'
                    }`}
                    style={{
                      width: '60px', 
                      height: '60px', 
                      backgroundColor: backgroundColor,
                      position: 'absolute',
                      zIndex: 5
                    }}
                  />
                  
                  {/* Logo image */}
                  <img 
                    src={logo} 
                    alt="Logo" 
                    style={{
                      width: '40px',
                      height: '40px',
                      zIndex: 10,
                      position: 'absolute'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Show name if selected */}
            {showName && name && (
              <div 
                className="text-center text-lg font-bold mt-3" 
                style={{ color: textColor || "#000000" }}
              >
                {name}
              </div>
            )}
            
            {/* Show URL if selected */}
            {showURL && content && type !== 'vcard' && (
              <div 
                className="text-center text-m font-semibold"
                style={{ color: textColor || "#000000" }}
              >
                {getFormattedContent()}
              </div>
            )}
          </div>

          <h2 className="mt-1 text-sm text-gray-400 font-medium mb-2">Preview</h2>

          {/* Add checkbox controls below the preview - new section for mobile */}
          <div className="w-full mb-6 flex flex-col md:hidden bg-white p-4 rounded-lg shadow-sm">
            <h3 className="text-m text-gray-700 font-semibold mb-3">Display Options:</h3>
            
            <div className="flex items-center mb-3">
              <input
                type="checkbox"
                id="mobile-showName"
                className="w-5 h-5 mr-3"
                checked={showName}
                onChange={(e) => setShowName(e.target.checked)}
              />
              <label htmlFor="mobile-showName" className="text-sm font-medium text-gray-700">Show Name</label>
            </div>
            
            {type !== 'vcard' && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="mobile-showURL"
                  className="w-5 h-5 mr-3"
                  checked={showURL}
                  onChange={(e) => setShowURL(e.target.checked)}
                />
                <label htmlFor="mobile-showURL" className="text-sm font-medium text-gray-700">{getShowContentLabel()}</label>
              </div>
            )}
          </div>

          <div className="mb-6 bg-white p-5 rounded-lg shadow-sm">
            <h3 className="block mt-1 text-m text-gray-700 font-semibold mb-2">Customize:</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* QR Code Color Picker - Extra Large Mobile Tap Area */}
              <div className="space-y-2">
                <label htmlFor="qr-color-picker" className="block text-sm font-medium text-gray-700">QR Code</label>
                <div className="flex items-center space-x-2">
                  <div className="relative w-12 h-12">
                    {/* The transparent color input with larger tap area */}
                    <input
                      id="qr-color-picker"
                      type="color"
                      value={foregroundColor}
                      onChange={(e) => setForegroundColor(e.target.value)}
                      className="absolute inset-0 w-12 h-12 opacity-0 cursor-pointer z-10"
                      style={{ touchAction: "manipulation" }}
                    />
                    {/* Visual indicator below the input */}
                    <div 
                      className="absolute inset-0 w-12 h-12 rounded-lg border border-gray-300"
                      style={{ backgroundColor: foregroundColor }}
                    />
                  </div>
                  <input
                    type="text"
                    className="w-full h-12 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    value={foregroundColor}
                    onChange={(e) => setForegroundColor(e.target.value)}
                    placeholder="#000000"
                  />
                </div>
              </div>
              
              {/* Background Color Picker - Extra Large Mobile Tap Area */}
              <div className="space-y-2">
                <label htmlFor="bg-color-picker" className="block text-sm font-medium text-gray-700">Background</label>
                <div className="flex items-center space-x-2">
                  <div className="relative w-12 h-12">
                    {/* The transparent color input with larger tap area */}
                    <input
                      id="bg-color-picker"
                      type="color"
                      value={backgroundColor}
                      onChange={(e) => setBackgroundColor(e.target.value)}
                      className="absolute inset-0 w-12 h-12 opacity-0 cursor-pointer z-10"
                      style={{ touchAction: "manipulation" }}
                    />
                    {/* Visual indicator below the input */}
                    <div 
                      className="absolute inset-0 w-12 h-12 rounded-lg border border-gray-300"
                      style={{ backgroundColor: backgroundColor }}
                    />
                    {backgroundColor.toLowerCase() === '#ffffff' && (
                      <div className="absolute inset-0 border border-gray-200 rounded-lg" />
                    )}
                  </div>
                  <input
                    type="text"
                    className="w-full h-12 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>
              
              {/* Text Color Picker - Extra Large Mobile Tap Area */}
              <div className="space-y-2">
                <label htmlFor="text-color-picker" className="block text-sm font-medium text-gray-700">Text</label>
                <div className="flex items-center space-x-2">
                  <div className="relative w-12 h-12">
                    {/* The transparent color input with larger tap area */}
                    <input
                      id="text-color-picker"
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="absolute inset-0 w-12 h-12 opacity-0 cursor-pointer z-10"
                      style={{ touchAction: "manipulation" }}
                    />
                    {/* Visual indicator below the input */}
                    <div 
                      className="absolute inset-0 w-12 h-12 rounded-lg border border-gray-300"
                      style={{ backgroundColor: textColor }}
                    />
                  </div>
                  <input
                    type="text"
                    className="w-full h-12 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    placeholder="#000000"
                  />
                </div>
              </div>
            </div>
            
            {/* Color Presets */}
            <div className="mt-5">
              <p className="text-sm text-gray-600 mb-2">Color Presets</p>
              <div className="flex flex-wrap gap-3">
                {/* Make preset buttons larger for mobile */}
                <button 
                  className={`h-12 w-12 rounded-lg border-2 flex items-center justify-center overflow-hidden ${
                    foregroundColor === '#000000' && backgroundColor === '#FFFFFF' && textColor === '#000000' 
                      ? 'border-blue-500' 
                      : 'border-gray-300'
                  }`}
                  onClick={() => {
                    setForegroundColor('#000000');
                    setBackgroundColor('#FFFFFF');
                    setTextColor('#000000');
                  }}
                  title="Default"
                >
                  <div className="w-full h-full bg-white flex items-center justify-center">
                    <div className="w-6 h-6 bg-black"></div>
                  </div>
                </button>
                
                {/* Blue preset with larger tap area */}
                <button 
                  className={`h-12 w-12 rounded-lg border-2 flex items-center justify-center overflow-hidden ${
                    foregroundColor === '#1E40AF' && backgroundColor === '#EFF6FF' && textColor === '#1E40AF' 
                      ? 'border-blue-500' 
                      : 'border-gray-300'
                  }`}
                  onClick={() => {
                    setForegroundColor('#1E40AF');
                    setBackgroundColor('#EFF6FF');
                    setTextColor('#1E40AF');
                  }}
                  title="Blue"
                >
                  <div className="w-full h-full bg-blue-50 flex items-center justify-center">
                    <div className="w-6 h-6 bg-blue-800"></div>
                  </div>
                </button>
                
                {/* Additional preset - Green */}
                <button 
                  className={`h-12 w-12 rounded-lg border-2 flex items-center justify-center overflow-hidden ${
                    foregroundColor === '#047857' && backgroundColor === '#ECFDF5' && textColor === '#047857' 
                      ? 'border-blue-500' 
                      : 'border-gray-300'
                  }`}
                  onClick={() => {
                    setForegroundColor('#047857');
                    setBackgroundColor('#ECFDF5');
                    setTextColor('#047857');
                  }}
                  title="Green"
                >
                  <div className="w-full h-full bg-green-50 flex items-center justify-center">
                    <div className="w-6 h-6 bg-green-700"></div>
                  </div>
                </button>
                
                {/* Additional preset - Red */}
                <button 
                  className={`h-12 w-12 rounded-lg border-2 flex items-center justify-center overflow-hidden ${
                    foregroundColor === '#DC2626' && backgroundColor === '#FEF2F2' && textColor === '#DC2626' 
                      ? 'border-blue-500' 
                      : 'border-gray-300'
                  }`}
                  onClick={() => {
                    setForegroundColor('#DC2626');
                    setBackgroundColor('#FEF2F2');
                    setTextColor('#DC2626');
                  }}
                  title="Red"
                >
                  <div className="w-full h-full bg-red-50 flex items-center justify-center">
                    <div className="w-6 h-6 bg-red-600"></div>
                  </div>
                </button>
              </div>
              
              {/* Shape Selection - Increase tap areas */}
              <p className="mt-4 text-sm text-gray-600 mb-2">Shape</p>
              <div className="mt-3 grid grid-cols-3 gap-3">
                {/* Square Shape */}
                <button
                  type="button"
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition duration-200 ${
                    shape === 'square' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setShape('square')}
                >
                  <div className="w-20 h-20 bg-gray-100 flex items-center justify-center mb-3">
                    <div className="w-12 h-12 bg-gray-800"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-800">Square</span>
                </button>
                
                {/* Rounded Shape */}
                <button
                  type="button"
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition duration-200 ${
                    shape === 'rounded' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setShape('rounded')}
                >
                  <div className="w-20 h-20 bg-gray-100 flex items-center justify-center mb-3">
                    <div className="w-12 h-12 bg-gray-800 rounded-lg"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-800">Rounded</span>
                </button>
                
                {/* Circle Shape */}
                <button
                  type="button"
                  className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition duration-200 ${
                    shape === 'circle' 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => setShape('circle')}
                >
                  <div className="w-20 h-20 bg-gray-100 flex items-center justify-center mb-3">
                    <div className="w-12 h-12 bg-gray-800 rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-800">Circle</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Submit button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-md">
        <div className="container mx-auto max-w-screen-lg">
          <div className="flex justify-between items-center max-w-5xl mx-auto">
            {/* Left side - Link */}
            <div>
              <Link 
                to="/bulk-upload" 
                className="text-gray-400 rounded hover:underline font-medium transition duration-200 text-center"
              >
                Have a few? Bulk upload
              </Link>
            </div>

            {/* Right side - Button */}
            <div>   
              {qrCodesCreated >= qrCodeLimit ? (
                <Link 
                  to="/upgrade" 
                  className="px-6 py-3 rounded font-semibold transition duration-200 bg-blue-700 hover:bg-blue-800 text-white"
                >
                  Upgrade to Create
                </Link>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className={`px-6 py-3 rounded font-semibold transition duration-200 ${
                    loading 
                      ? 'bg-blue-600 text-white opacity-75 cursor-wait' 
                      : 'bg-blue-700 hover:bg-blue-800 text-white'
                  }`}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </span>
                  ) : (
                    'Create QR Code'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateQRCode;